import React from "react";
import { UserOutlined } from "@ant-design/icons";
import { uri_img } from "../../assets/utils/http-request";
import { EnrolleeConsumption } from "./EnrolleeConsumption";
import "./enrollee-identity-strip.css";

const CHECKIN_TYPE_MAP = {
  1: "In-Patient",
  2: "Out-Patient",
  3: "Antenatal",
  4: "Dental",
  5: "Optical",
  in: "In-Patient",
  out: "Out-Patient",
  "in-patient": "In-Patient",
  "out-patient": "Out-Patient",
};

function capitalizePart(part) {
  if (part == null || part === "") return "";
  const text = String(part);
  return text.__capitalize?.() || text;
}

function calculateAge(birthDate) {
  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

function nonempty(value) {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "--" || text.toLowerCase() === "null") return "";
  return text;
}

export function formatEnrolleeDisplayName(client) {
  const surname = client?.surname ? String(client.surname).toUpperCase() : "";
  const given = [client?.first_name, client?.middle_name]
    .filter(Boolean)
    .map(capitalizePart)
    .join(" ");
  if (surname && given) return `${surname}, ${given}`;
  if (surname) return surname;
  if (given) return given;
  if (client?.patient_name) return String(client.patient_name);
  return "Unknown patient";
}

export function formatEnrolleeAge(client) {
  const explicitAge = Number(client?.age);
  if (Number.isFinite(explicitAge) && explicitAge >= 0 && client?.age !== "" && client?.age != null) {
    return explicitAge;
  }
  const dob = client?.date_of_birth || client?.dob;
  if (!dob) return null;
  const computed = calculateAge(dob);
  if (Number.isFinite(computed) && computed >= 0 && computed < 130) return computed;
  return null;
}

/** Member contract status — never premium type. */
export function formatEnrolleeMemberStatus(client) {
  const raw = client?.status ?? client?.enrollee_status;
  if (raw === undefined || raw === null || raw === "") return "—";
  if (raw === true || Number(raw) === 1 || String(raw).toLowerCase() === "active") return "Active";
  if (raw === false || Number(raw) === 0 || ["inactive", "expired", "disabled"].includes(String(raw).toLowerCase())) {
    return "Inactive";
  }
  return String(raw);
}

export function formatEnrolleeDate(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function formatEnrolleeDateTime(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return `${formatEnrolleeDate(dateValue)} ${date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

export function resolveEnrolleePlanLabel(client, override) {
  if (nonempty(override)) return nonempty(override);
  return nonempty(client?.plan_name)
    || nonempty(client?.planName)
    || nonempty(client?.insurance_package)
    || nonempty(client?.enrollee_insurance_package)
    || "—";
}

export function resolveEnrolleeOrgName(client) {
  return nonempty(client?.org_name)
    || nonempty(client?.organization)
    || nonempty(client?.corporation_name)
    || nonempty(client?.org)
    || "—";
}

export function resolveEnrolleeDateJoined(client) {
  return client?.date_joined
    || client?.enrollee_record_date
    || client?.joined_date
    || client?.enrollee_date
    || null;
}

export function resolveEnrolleeTypeLabel(record) {
  if (!record || typeof record !== "object") return null;

  const namedCandidates = [
    record.checkin_type_name,
    record.checkinName,
    record.checkin_type,
    record.checkinTypeName,
    record.checkin_type_label,
  ];

  for (const named of namedCandidates) {
    if (named == null) continue;
    const text = String(named).trim();
    if (!text) continue;
    if (text.toLowerCase() === "unknown") continue;
    if (Number.isNaN(Number(text))) return text.__capitalize?.() || text;
    if (CHECKIN_TYPE_MAP[text] != null) return CHECKIN_TYPE_MAP[text];
    if (CHECKIN_TYPE_MAP[Number(text)] != null) return CHECKIN_TYPE_MAP[Number(text)];
  }

  const raw = record.checkinType ?? record.checkin_type_id ?? null;
  if (raw == null || raw === "") return null;
  if (CHECKIN_TYPE_MAP[raw] != null) return CHECKIN_TYPE_MAP[raw];
  const asNum = Number(raw);
  if (!Number.isNaN(asNum) && CHECKIN_TYPE_MAP[asNum] != null) return CHECKIN_TYPE_MAP[asNum];
  const asText = String(raw).trim();
  if (!asText) return null;
  if (CHECKIN_TYPE_MAP[asText.toLowerCase()] != null) return CHECKIN_TYPE_MAP[asText.toLowerCase()];
  if (Number.isNaN(Number(asText))) return asText.__capitalize?.() || asText;
  return null;
}

function FactRow({ items, className }) {
  const visible = (items || []).filter((item) => item != null && item !== false && item !== "");
  if (!visible.length) return null;
  return (
    <div className={className}>
      {visible.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 ? <span className="enrollee-identity-strip__sep" aria-hidden>·</span> : null}
          {typeof item === "string" || typeof item === "number" ? <span>{item}</span> : item}
        </React.Fragment>
      ))}
    </div>
  );
}

function Labeled({ label, value }) {
  return (
    <span className="enrollee-identity-strip__labeled">
      <span className="enrollee-identity-strip__label">{label}</span>
      {value || "—"}
    </span>
  );
}

const RELATIONSHIP_LABELS = {
  spouse: "Spouse",
  child: "Child",
  son: "Child",
  daughter: "Child",
};

/** "Principal", "Dependent · Spouse", "Dependent · Child", "Dependent · Sibling", or null when role is unknown. */
export function resolveEnrolleeRoleLabel(client) {
  const role = nonempty(client?.role).toLowerCase();
  if (!role) return null;
  if (role === "principal") return "Principal";

  const relationship = nonempty(client?.relationship).toLowerCase();
  if (!relationship || relationship === "other") return "Dependent";
  const mapped = RELATIONSHIP_LABELS[relationship];
  return `Dependent · ${mapped || capitalizePart(relationship)}`;
}

export function resolveEnrolleeInsuranceNo(client) {
  return nonempty(client?.insurance_no)
    || nonempty(client?.insuranceNo)
    || nonempty(client?.member_id)
    || nonempty(client?.memberId);
}

/**
 * Enrollee identity header. Required: photo, name, age, plan, date joined, organization.
 * Includes View consumption (fetch on click). Extra CTAs via `actions`.
 */
export default function EnrolleeIdentityStrip({
  client = {},
  typeLabel,
  showTypeBadge = true,
  showMissingType = false,
  planLabel,
  facilityName,
  datetime,
  itemCount,
  extraFacts,
  extraSecondaryFacts,
  statusChip,
  actions,
  children,
  variant = "card",
  compact = false,
  showConsumption = true,
  consumptionTriggerVariant,
  className = "",
  ariaLabel = "Enrollee identity",
}) {
  const displayName = formatEnrolleeDisplayName(client);
  const age = formatEnrolleeAge(client);
  const photoSrc = client?.pic_name ? `${uri_img}${client.pic_name}` : null;
  const insuranceNo = resolveEnrolleeInsuranceNo(client);
  const policyNo = nonempty(client?.policy_no) || nonempty(client?.policyNo);
  const plan = resolveEnrolleePlanLabel(client, planLabel);
  const orgName = resolveEnrolleeOrgName(client);
  const joined = formatEnrolleeDate(resolveEnrolleeDateJoined(client));
  const memberStatus = formatEnrolleeMemberStatus(client);
  const resolvedType = typeLabel !== undefined && typeLabel !== null && typeLabel !== ""
    ? typeLabel
    : resolveEnrolleeTypeLabel(client);
  const roleLabel = resolveEnrolleeRoleLabel(client);
  const facility = facilityName
    ?? nonempty(client?.facility_name)
    ?? nonempty(client?.facilityName)
    ?? null;
  const when = datetime !== undefined
    ? formatEnrolleeDateTime(datetime)
    : formatEnrolleeDateTime(client?.checkin_date || client?.checkinDate);
  const countLabel = typeof itemCount === "number"
    ? `${itemCount} item${itemCount === 1 ? "" : "s"}`
    : null;

  const classes = [
    "enrollee-identity-strip",
    `enrollee-identity-strip--${variant}`,
    compact ? "enrollee-identity-strip--compact" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <div className={classes} aria-label={ariaLabel}>
      <div className="enrollee-identity-strip__row">
        <div className="enrollee-identity-strip__lead">
          {photoSrc ? (
            <img className="enrollee-identity-strip__photo" src={photoSrc} alt="" />
          ) : (
            <div className="enrollee-identity-strip__icon" aria-hidden>
              <UserOutlined />
            </div>
          )}
          <div className="enrollee-identity-strip__identity">
            <div className="enrollee-identity-strip__title-row">
              <h2 className="enrollee-identity-strip__name">{displayName}</h2>
              {roleLabel ? (
                <span className="enrollee-identity-strip__role-badge" title="Enrollee role">
                  {roleLabel}
                </span>
              ) : null}
              {showTypeBadge ? (
                resolvedType ? (
                  <span className="enrollee-identity-strip__type-badge" title="Check-in type">
                    {resolvedType}
                  </span>
                ) : showMissingType ? (
                  <span className="enrollee-identity-strip__type-badge is-missing" title="Check-in type">
                    Type unknown
                  </span>
                ) : null
              ) : null}
            </div>
            <FactRow
              className="enrollee-identity-strip__facts"
              items={[
                <Labeled key="age" label="Age" value={age != null ? String(age) : "—"} />,
                <Labeled key="plan" label="Plan" value={plan} />,
                <Labeled key="joined" label="Joined" value={joined || "—"} />,
                <Labeled key="org" label="Org" value={orgName} />,
                ...(extraFacts || []),
              ]}
            />
            <FactRow
              className="enrollee-identity-strip__facts enrollee-identity-strip__facts--secondary"
              items={[
                insuranceNo || null,
                policyNo ? `Policy ${policyNo}` : null,
                `Member: ${memberStatus}`,
                facility,
                when,
                countLabel,
                statusChip ? (
                  <span className="enrollee-identity-strip__chip">{statusChip}</span>
                ) : null,
                ...(extraSecondaryFacts || []),
              ]}
            />
          </div>
        </div>
        {(showConsumption && insuranceNo) || actions ? (
          <div
            className="enrollee-identity-strip__aside"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {showConsumption && insuranceNo ? (
              <EnrolleeConsumption
                userId={insuranceNo}
                enrolleeData={client}
                triggerVariant={consumptionTriggerVariant ?? (compact ? "link" : "button")}
                triggerLabel="View consumption"
              />
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}
